import { gradeLevelStorage } from "@/lib/localStorage";
import { useMemo } from "react";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { getGradeTierProfile, resolveGradeLevel, type GradeTierProfile } from "@shared/gradeTier";

/**
 * Tiered replacement for `useKidsMode()`. Returns a full profile describing
 * what UI affordances the current user's grade-level should expose.
 *
 * Prefer this over `useKidsMode` when you need anything more nuanced than
 * "is this a kid": question count caps, allowed question types, difficulty
 * gating, UI density hints.
 *
 * Reads from the same source as `useKidsMode`:
 *   1. UserPreferencesContext (`preferences.grade_level` / `gradeLevel`)
 *   2. localStorage `novari_last_grade_level` fallback
 *
 * Falls back to the `shs` tier when unknown — that's the median user.
 */
export function useGradeTier(): GradeTierProfile {
    const { preferences } = useUserPreferences();
    const lastGrade =
        typeof window !== "undefined"
            ? gradeLevelStorage.load()
            : null;
    const gradeLevel = resolveGradeLevel(preferences, lastGrade);

    // getGradeTierProfile builds a brand-new object literal on every call, so
    // without memoizing on the actual primitive input, every render of every
    // consumer returns a *different* reference even when the tier hasn't
    // changed. Any consumer that puts this profile in a useEffect/useMemo
    // dependency array — as QuizCreator does to seed its form fields — sees
    // it as "changed" on every render and re-runs, which in QuizCreator's case
    // reset the topic input back to its default on every keystroke (typing
    // triggers a re-render, which re-ran the seeding effect, which cleared
    // what was just typed). Memoizing on the primitive gradeLevel fixes this
    // for every consumer of the hook, not just one call site.
    return useMemo(() => getGradeTierProfile(gradeLevel), [gradeLevel]);
}
